import {
  mysqlTable,
  int,
  varchar,
  text,
  boolean,
  timestamp,
  datetime,
  date,
  mysqlEnum,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { countries } from "./core";

/**
 * users — the identity table. Better Auth maps its `user` model onto THIS table
 * (decision 2026-06-23: map-onto-legacy-users); BA additionally owns session/account/
 * verification tables (added in Batch 1.1 Part B / auth-wiring step). `password` is kept
 * for credential migration. `permission_sync` (0 = effective-permissions stale) drives the
 * permission-sync cron (reskin-docs/03 §7).
 *
 * Ported line-by-line from the base migration L61–93 (+ company_id FK added L168–169).
 * Cross-checked against _ide_helper_models.php (User, L5003) — no columns beyond the create
 * block. Deferred FK: country_id → countries.
 */
export const users = mysqlTable("users", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  name: varchar("name", { length: 255 }).notNull(),
  email: varchar("email", { length: 255 }).unique(),
  // Better Auth requires an emailVerified flag on the user model (mapped onto this table).
  emailVerified: boolean("email_verified").default(false).notNull(),
  password: varchar("password", { length: 255 }), // nullable: BA credentials live in `account`, not here
  twoFactorSecret: text("two_factor_secret"),
  twoFactorRecoveryCodes: text("two_factor_recovery_codes"),
  twoFactorConfirmed: boolean("two_factor_confirmed").default(false).notNull(),
  twoFactorEmailConfirmed: boolean("two_factor_email_confirmed").default(false).notNull(),
  image: varchar("image", { length: 255 }),
  mobile: varchar("mobile", { length: 255 }),
  gender: mysqlEnum("gender", ["male", "female", "others"]),
  salutation: mysqlEnum("salutation", ["mr", "mrs", "miss", "dr", "sir", "madam"]),
  locale: varchar("locale", { length: 255 }).default("en").notNull(),
  status: mysqlEnum("status", ["active", "deactive"]).default("active").notNull(),
  login: mysqlEnum("login", ["enable", "disable"]).default("enable").notNull(),
  onesignalPlayerId: text("onesignal_player_id"),
  lastLogin: timestamp("last_login"),
  emailNotifications: boolean("email_notifications").default(true).notNull(),
  countryId: int("country_id", { unsigned: true }).references(() => countries.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }), // FK → countries
  darkTheme: boolean("dark_theme").default(false).notNull(),
  rtl: boolean("rtl").default(false).notNull(),
  twoFaVerifyVia: mysqlEnum("two_fa_verify_via", ["email", "google_authenticator", "both"]),
  twoFactorCode: varchar("two_factor_code", { length: 255 }), // when authenticator is email
  twoFactorExpiresAt: datetime("two_factor_expires_at"),
  adminApproval: boolean("admin_approval").default(true).notNull(),
  permissionSync: boolean("permission_sync").default(true).notNull(),
  googleCalendarStatus: boolean("google_calendar_status").default(true).notNull(),
  rememberToken: varchar("remember_token", { length: 100 }),
  // Added by later up() ALTER migrations (reconciled to final shape):
  customisedPermissions: boolean("customised_permissions").default(false).notNull(), // 2022_09_13
  stripeId: varchar("stripe_id", { length: 255 }), // 2022_12_29
  pmType: varchar("pm_type", { length: 255 }), // 2022_12_29
  pmLastFour: varchar("pm_last_four", { length: 4 }), // 2022_12_29
  trialEndsAt: timestamp("trial_ends_at"), // 2022_12_29
  countryPhonecode: int("country_phonecode"), // 2023_03_23
  inactiveDate: date("inactive_date"), // 2024_03_07
  twitterId: varchar("twitter_id", { length: 255 }), // 2024_08_09
  isClientContact: int("is_client_contact", { unsigned: true }), // FK → client_contacts (deferred) — 2024_11_05
  headers: text("headers"), // 2024_02_07
  registerIp: varchar("register_ip", { length: 255 }), // 2024_02_07
  locationDetails: text("location_details"), // 2024_04_15
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
